import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import NavBarBackOffice from '../components/backOffice/Navbar';
import { GlobalContext } from '../context/GlobalStateProvider';
import { pages } from '../constants/Consts';
import DifficultySummaryHome from '../components/HomePage/DifficultySummaryHome';
import CommentsCard from '../components/backOffice/CommentsCard';
import { BestBouldersList } from '../components/backOffice/BestBouldersList';
import GeneralStatsSection from '../components/backOffice/GeneralStatsSection';
import SectorsOverviewSection from '../components/backOffice/SectorsOverviewSection';
import GymSelectionModal from '../components/backOffice/GymSelectionModal';
import { useHomePageData } from '../hooks/useHomePageData';
import { useGymSelection } from '../hooks/useGymSelection';

const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`bg-grey-200 rounded-lg animate-pulse ${className}`} />
);

const HomePageSkeleton: React.FC = () => (
    <>
        {/* General stats section skeleton */}
        <div className="flex flex-row gap-4 w-full">
            <div className="flex flex-col bg-white rounded-lg p-4 gap-4 flex-1">
                <SkeletonBlock className="h-6 w-72" />
                <SkeletonBlock className="h-4 w-96" />
                <div className="flex flex-row gap-6">
                    <SkeletonBlock className="h-20 flex-1 rounded-lg" />
                    <SkeletonBlock className="h-20 flex-1 rounded-lg" />
                    <SkeletonBlock className="h-20 flex-1 rounded-lg" />
                    <SkeletonBlock className="h-20 flex-1 rounded-lg" />
                </div>
            </div>
            <div className="flex flex-col bg-white rounded-lg p-3 gap-3 w-72">
                <SkeletonBlock className="h-5 w-40" />
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-3/4" />
            </div>
        </div>

        {/* Difficulty summary skeleton */}
        <div className="bg-white rounded-lg p-4 flex flex-col gap-4 w-full">
            <SkeletonBlock className="h-6 w-52" />
            <SkeletonBlock className="h-4 w-80" />
            <div className="flex gap-3 mt-2">
                <SkeletonBlock className="h-16 w-16 rounded-md" />
                <SkeletonBlock className="h-16 w-16 rounded-md" />
                <SkeletonBlock className="h-16 w-16 rounded-md" />
                <SkeletonBlock className="h-16 w-16 rounded-md" />
                <SkeletonBlock className="h-16 w-16 rounded-md" />
                <SkeletonBlock className="h-16 w-16 rounded-md" />
            </div>
        </div>

        {/* Sectors overview skeleton */}
        <div className="bg-white rounded-lg p-4 flex flex-col gap-4 w-full">
            <SkeletonBlock className="h-6 w-48" />
            <SkeletonBlock className="h-4 w-72" />
            <div className="flex flex-col gap-2 mt-2">
                <SkeletonBlock className="h-10 w-full rounded-md" />
                <SkeletonBlock className="h-10 w-full rounded-md" />
                <SkeletonBlock className="h-10 w-full rounded-md" />
                <SkeletonBlock className="h-10 w-full rounded-md" />
            </div>
            <div className="flex gap-4 mt-2">
                <SkeletonBlock className="h-20 w-40 rounded-lg" />
                <SkeletonBlock className="h-20 w-40 rounded-lg" />
            </div>
        </div>

        {/* Best boulders skeleton */}
        <div className="bg-white rounded-lg p-4 flex flex-col gap-4 w-full">
            <SkeletonBlock className="h-6 w-56" />
            <SkeletonBlock className="h-4 w-64" />
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-10 w-10 rounded-full flex-shrink-0" />
                    <SkeletonBlock className="h-5 flex-1" />
                </div>
                <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-10 w-10 rounded-full flex-shrink-0" />
                    <SkeletonBlock className="h-5 flex-1" />
                </div>
                <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-10 w-10 rounded-full flex-shrink-0" />
                    <SkeletonBlock className="h-5 flex-1" />
                </div>
            </div>
        </div>

        {/* Comments skeleton */}
        <div className="bg-white rounded-lg p-4 flex flex-col gap-4 w-full">
            <SkeletonBlock className="h-6 w-40" />
            <div className="flex gap-3 items-start">
                <SkeletonBlock className="h-10 w-10 rounded-full flex-shrink-0" />
                <div className="flex flex-col gap-2 flex-1">
                    <SkeletonBlock className="h-4 w-32" />
                    <SkeletonBlock className="h-4 w-full" />
                </div>
            </div>
            <div className="flex gap-3 items-start">
                <SkeletonBlock className="h-10 w-10 rounded-full flex-shrink-0" />
                <div className="flex flex-col gap-2 flex-1">
                    <SkeletonBlock className="h-4 w-40" />
                    <SkeletonBlock className="h-4 w-3/4" />
                </div>
            </div>
        </div>
    </>
);

const HomePageBO: React.FC = () => {
    const navigate = useNavigate();
    const globalContext = useContext(GlobalContext);
    const { currentGym, setCurrentGym, setCurrentPage, setGymsFilterInfo, gymsFilterInfo, routeSetterInfo, setRouteSetterInfo, user } = globalContext;

    const {
        gymStats, bestBoulders, comments,
        sectorsOverview, latestOpeningSector, upcomingClosingSectors, zeroBouldersSectors,
        isLoading,
    } = useHomePageData(user, currentGym);

    const { isModalOpen, setIsModalOpen, handleGymSelect } = useGymSelection(user, setCurrentGym, setGymsFilterInfo);

    useEffect(() => {
        document.title = "Accueil";
    }, []);

    useEffect(() => {
        setCurrentPage(pages.accueil);
    }, []);

    return (
        <div className='outerContainer'>
            <NavBarBackOffice
                profilePicture={routeSetterInfo?.picture}
                firstName={routeSetterInfo?.firstName}
                lastName={routeSetterInfo?.lastName}
                gyms={currentGym && currentGym.name}
            />
            <div className="flex flex-col items-start gap-5 overflow-y-auto p-8 flex-1">
                <div className="flex items-center gap-2 text-backoffice-800">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.0607 2.24989C12.4749 1.6641 11.5251 1.6641 10.9393 2.24989L0.96967 12.2196C0.676777 12.5125 0.676777 12.9873 0.96967 13.2802C1.26256 13.5731 1.73744 13.5731 2.03033 13.2802L3 12.3105V20.2499C3 21.4925 4.00736 22.4999 5.25 22.4999H18.75C19.9926 22.4999 21 21.4925 21 20.2499V12.3105L21.9697 13.2802C22.2626 13.5731 22.7374 13.5731 23.0303 13.2802C23.3232 12.9873 23.3232 12.5125 23.0303 12.2196L19.5 8.68923V3.74989C19.5 3.33567 19.1642 2.99989 18.75 2.99989H17.25C16.8358 2.99989 16.5 3.33567 16.5 3.74989V5.68923L13.0607 2.24989ZM19.5 10.8105V20.2499C19.5 20.6641 19.1642 20.9999 18.75 20.9999H5.25C4.83579 20.9999 4.5 20.6641 4.5 20.2499V10.8105L12 3.31055L19.5 10.8105Z" fill="#1D5073"/>
                    </svg>
                    <div className="bold large3">
                        <p>Tableau de bord - {currentGym && currentGym.name}</p>
                    </div>
                </div>

                {isLoading ? (
                    <HomePageSkeleton />
                ) : (
                    <>
                        <div className="animate-fadeIn w-full flex flex-col items-start gap-5">
                            <GeneralStatsSection gymStats={gymStats} />

                            <div
                                className="group relative bg-white rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:ring-1 hover:ring-backoffice-200 hover:bg-grey-25 w-full"
                                onClick={() => navigate('/circuits-overview')}
                            >
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-backoffice-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <div className='bold large3 flex items-baseline'>Répartition des blocs</div>
                                    <div className='text-grey-500 regular normal'>Les blocs de la salle classés par difficulté et sous-difficulté</div>
                                </div>
                                {gymStats &&
                                    <div className='mt-4'>
                                        <DifficultySummaryHome
                                            difficultyheaders={gymStats.difficulties}
                                            totalBoulderCount={gymStats.totalActiveRoutes}
                                        />
                                    </div>
                                }
                            </div>

                            <SectorsOverviewSection
                                sectorsOverview={sectorsOverview}
                                zeroBouldersSectors={zeroBouldersSectors}
                                upcomingClosingSectors={upcomingClosingSectors}
                                latestOpeningSector={latestOpeningSector}
                            />

                            <div className="bg-white rounded-lg p-4 flex flex-col gap-4 w-full">
                                <div>
                                    <div className='bold large3 flex items-baseline'>Les blocs les plus essayés</div>
                                    <div className='text-grey-500 regular normal'>Classés par le nombre d'essais par jour</div>
                                </div>
                                {bestBoulders && <BestBouldersList boulders={bestBoulders} />}
                            </div>

                            <div className="bg-white rounded-lg p-4 flex flex-col gap-4 w-full">
                                <div>
                                    <div className='bold large3 flex items-baseline'>Commentaires</div>
                                </div>
                                <div className='flex flex-col gap-4 w-full'>
                                    {(comments && comments.length > 0) ?
                                        comments.map((comment) =>
                                            <CommentsCard
                                                key={comment.commentId}
                                                profilePictureUser={comment.profilePictureUser}
                                                boulderId={comment.boulderId}
                                                boulderName={comment.boulderName}
                                                boulderPicture={comment.boulderPicture}
                                                comment={comment.comment}
                                                commentId={comment.commentId}
                                                createdAt={comment.createdAt}
                                                difficultySvg={comment.difficultySvg}
                                                firstNameUser={comment.firstNameUser}
                                                lastNameUser={comment.lastNameUser}
                                                level={comment.level}
                                                sectorName={comment.sectorName}
                                            />
                                        )
                                        :
                                        <div className='no-routes-msg light large2'>
                                            Pas encore de commentaire
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <GymSelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                gymsFilterInfo={gymsFilterInfo}
                currentGym={currentGym}
                onGymSelect={handleGymSelect}
            />
        </div>
    );
}

export default HomePageBO;
